import { mapUtil } from '@stormwater-software/common';
import db, {
  getOfflineEsriPositionables,
  getOfflineProjectDocs,
  getOfflineSiteMapUploads,
} from '../../db/db';
import { getDownloadUrl } from '../../utils/api/upload';
import { isLegendItemType } from '../../components/MapEditor/Positionable/legendItemType';
import { loadImageDimensions, SiteMapPrintError } from './siteMapPrintPdf';

export type LegendType = 'control_measure' | 'pollutant';
const LEGEND_TYPES: LegendType[] = ['control_measure', 'pollutant'];

export const ESRI_MAP_TYPES = ['gis_esri', 'gis_image_esri'];

/** One printable map, in the order it will appear in the PDF. */
export interface SiteMapPrintDoc {
  id: string | number;
  name: string;
  hasEsri?: boolean;
  ref?: any;
}

/** Everything the offscreen `<Map>` needs to draw one page. */
export interface SiteMapPrintData {
  mapImgSrc: string;
  dataSources: any[];
  imageDimensions: { width: number; height: number };
  esri?: { projectId: number; projectUpload: any; rows: any[] };
}

/** Where a print run gets its maps: IndexedDB offline, caller-supplied data online. */
export interface SiteMapPrintSource {
  listMaps: () => Promise<SiteMapPrintDoc[]>;
  getMapData: (doc: SiteMapPrintDoc) => Promise<SiteMapPrintData>;
}

/**
 * When printing from ellipsis of project header, all site maps are printed
 */
export const getOfflineSiteMaps = async (projectId: number) => {
  const docs = await getOfflineProjectDocs(Number(projectId));
  const documents =
    (docs?.documentTypes || []).find(
      (docType: any) => docType.name === 'Active Site Maps',
    )?.documents || [];

  return documents.filter(
    (doc: any) =>
      !doc.archived_at &&
      doc.ProjectUploadModel?.upload_id != null &&
      mapUtil.isSupportedMimeType(doc.mime_type),
  );
};

/** Newest placement per edit chain, mirroring the server's DISTINCT ON. */
const collapseEditChains = (rows: any[]) => {
  const byHead = new Map<number, any>();

  rows.forEach((row) => {
    const headId = row.source_positionable_id ?? row.id;
    const prev = byHead.get(headId);

    if (
      !prev ||
      (row.start_inspection_id ?? 0) > (prev.start_inspection_id ?? 0)
    ) {
      byHead.set(headId, row);
    }
  });

  return Array.from(byHead.values());
};

/** Placements visible at `inspectionId`, or the currently active ones when printing from a project. */
const isVisible = (row: any, inspectionId?: number | null) => {
  if (row.marked_deleted) {
    return false;
  }

  if (!inspectionId) {
    return row.end_inspection_id == null;
  }

  return (
    (row.start_inspection_id == null ||
      row.start_inspection_id <= inspectionId) &&
    (row.end_inspection_id == null || row.end_inspection_id > inspectionId)
  );
};

/** The project_inspection_uploads row joining this map to this inspection — findings hang off it. */
const getInspectionUploadId = async (
  projectId: number,
  projectUploadId: number,
  inspectionId: number,
) => {
  const row = await db.projectInspectionUploads
    .where('project_id')
    .equals(Number(projectId))
    .and((item: any) => item.inspection_id === Number(inspectionId))
    .first();

  const match = (row?.data || []).find(
    (upload: any) =>
      Number(upload.project_upload_id) === Number(projectUploadId),
  );

  return match?.id ?? null;
};

/** Cached ESRI features plus the georeference they are projected with, if the map has any. */
const loadEsriLayer = async (projectId: number, projectUploadId: number) => {
  const rows = await getOfflineEsriPositionables(
    projectId,
    projectUploadId,
  ).catch(() => null);

  if (!rows?.length) {
    return undefined;
  }

  const uploads = await getOfflineSiteMapUploads(projectId).catch(() => null);
  const projectUpload = (uploads || []).find(
    (upload: any) => Number(upload.id) === Number(projectUploadId),
  );

  return projectUpload
    ? { projectId: Number(projectId), projectUpload, rows }
    : undefined;
};

/** Builds one map's `<Map>` props entirely from IndexedDB, so no network is needed. */
export const getOfflineSiteMapPrintData = async (
  projectId: number,
  projectUploadId: number,
  uploadGuid: string,
  inspectionId?: number | null,
): Promise<SiteMapPrintData> => {
  const mapImgSrc = uploadGuid ? getDownloadUrl(uploadGuid) : null;

  if (!mapImgSrc) {
    throw new SiteMapPrintError(
      'This site map image is not available offline. Download the project while online, then print.',
    );
  }

  const [mapPlacements, legendItems, notesRow, imageDimensions, esri] =
    await Promise.all([
      db.positionables
        .where('target_id')
        .equals(Number(projectUploadId))
        .and((row: any) => row.project_id === Number(projectId))
        .toArray(),
      db.legendItems.where('project_id').equals(Number(projectId)).toArray(),
      db.mapNotes
        .get([Number(projectId), Number(projectUploadId)])
        .catch(() => null),
      loadImageDimensions(mapImgSrc),
      loadEsriLayer(projectId, projectUploadId),
    ]);

  const legendItemById = new Map<number, any>(
    legendItems.map((item: any) => [item.id, item]),
  );
  const visible = collapseEditChains(
    mapPlacements.filter((row: any) => isVisible(row, inspectionId)),
  );
  const typeOf = (row: any): LegendType | null =>
    legendItemById.get(row.positionable_id)?.type ?? null;

  const dataSources: any[] = LEGEND_TYPES.map((type) => ({
    positionableType: type,
    targetType: 'project_uploads',
    targetId: Number(projectUploadId),
    positionableInstances: visible
      .filter(
        (row) =>
          isLegendItemType(row.positionable_type) && typeOf(row) === type,
      )
      .map((row) => ({ ...row, positionable_type: type })),
    positionables: legendItems.filter((item: any) => item.type === type),
  }));

  const noteInstances = visible.filter(
    (row) => row.positionable_type === 'commentables',
  );

  if (noteInstances.length) {
    const noteById = new Map<number, any>(
      (notesRow?.data || []).map((note: any) => [note.id, note]),
    );

    dataSources.push({
      positionableType: 'commentables',
      targetType: 'project_uploads',
      targetId: Number(projectUploadId),
      positionableInstances: noteInstances,
      // The source model is keyed by the placement it belongs to, as the print worker does.
      positionables: noteInstances
        .map((row) => {
          const note = noteById.get(row.positionable_id);

          return note ? { ...note, id: row.positionable_id } : null;
        })
        .filter(Boolean),
    });
  }

  // Findings belong to an inspection; the project-level print omits them, as the server does.
  if (inspectionId) {
    const inspectionUploadId = await getInspectionUploadId(
      projectId,
      projectUploadId,
      inspectionId,
    );

    if (inspectionUploadId) {
      const findingPlacements = collapseEditChains(
        (
          await db.positionables
            .where('target_id')
            .equals(Number(inspectionUploadId))
            .and((row: any) => row.project_id === Number(projectId))
            .toArray()
        ).filter(
          (row: any) =>
            row.positionable_type === 'findings' &&
            isVisible(row, inspectionId),
        ),
      );

      if (findingPlacements.length) {
        const findings = await db.findings
          .where('id')
          .anyOf(findingPlacements.map((row) => row.positionable_id))
          .toArray();

        dataSources.push({
          positionableType: 'findings',
          targetType: 'project_inspection_uploads',
          targetId: Number(inspectionUploadId),
          positionableInstances: findingPlacements,
          positionables: findings,
        });
      }
    }
  }

  return { mapImgSrc, dataSources, imageDimensions, esri };
};

export interface OfflineSiteMapPrintInput {
  projectId: number;
  docs?: any[];
  inspectionId?: number | null;
}

/**
 * Print for online mode;
 * backed by IndexedDB — used when the device has no connection.
 */
export const createOfflineSiteMapSource = ({
  projectId,
  docs,
  inspectionId,
}: OfflineSiteMapPrintInput): SiteMapPrintSource => ({
  listMaps: async () => {
    const cached = docs || (await getOfflineSiteMaps(projectId));

    return cached.map(
      (doc: any): SiteMapPrintDoc => ({
        id: doc?.ProjectUploadModel?.id,
        name: doc?.name || 'Site Map',
        hasEsri: ESRI_MAP_TYPES.includes(
          doc?.ProjectUploadModel?.site_map_type,
        ),
        ref: doc,
      }),
    );
  },
  getMapData: ({ id, ref }) =>
    getOfflineSiteMapPrintData(
      projectId,
      id as number,
      ref?.GUID,
      inspectionId,
    ),
});

export interface SiteMapPrintInput {
  id: string | number;
  name: string;
  mapImgSrc: string;
  dataSources: any[];
  imageDimensions?: { width: number; height: number };
}

/**
 * Print for online mode; PS: not used anywhere yet
 * but incase in future if we want to shift printing maps to client side this can be used
 */
export const createDataSiteMapSource = (
  maps: SiteMapPrintInput[],
): SiteMapPrintSource => {
  const byId = new Map(maps.map((map) => [map.id, map]));

  return {
    listMaps: async () => maps.map(({ id, name }) => ({ id, name })),
    getMapData: async ({ id }): Promise<SiteMapPrintData> => {
      const map = byId.get(id);

      if (!map?.mapImgSrc) {
        throw new SiteMapPrintError('This site map image is not available.');
      }

      return {
        mapImgSrc: map.mapImgSrc,
        dataSources: map.dataSources || [],
        imageDimensions:
          map.imageDimensions || (await loadImageDimensions(map.mapImgSrc)),
      };
    },
  };
};
